import { getSubscriptionApiClient } from "../../shared/apis/sidekick/api-factories";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiResponseErrorHandler } from "@shared/apis/sidekick/api-error-handler";
import { toast } from "@shared/services/toast";
import { appLogger } from "@shared/services/app-logger";
import { buildAdminGetUserSubscriptionStatusQueryKey } from "./useAdminGetUserSubscription";

type Props = {
  onSuccess?: () => void;
};

type Params = {
  userId: string;
  trialDays: number | null;
  trialEndDate: Date | null;
};

export const useAdminUpdateUserSubscriptionTrialDays = (props?: Props) => {
  const { onSuccess } = props || {};
  const api = getSubscriptionApiClient();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: ["admin-subscription", "update-trial-days"],
    mutationFn: async ({ userId, trialDays, trialEndDate }: Params) => {
      const response = await api.subscriptionControllerAdminUpdateTrialDays({
        userId,
        updateTrialPeriodRequestBody: {
          trialDays: typeof trialDays === "number" ? trialDays : undefined,
          trialEnd: trialEndDate ? trialEndDate.toISOString() : undefined,
        },
      });

      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: buildAdminGetUserSubscriptionStatusQueryKey({ userId }),
        });
      }, 1000);
      toast.success(
        `User subscription trial days has been successfully updated.`,
      );
      onSuccess?.();
      return response;
    },
    onError: async (error, { userId }) => {
      const apiError = await apiResponseErrorHandler(error);
      if (apiError.status >= 400 && apiError.status < 500) {
        toast.error(apiError.errors[0]);
        return;
      }
      toast.error(
        `Failed to update user subscription trial days with user ID ${userId}. If the issue persists, please contact support.`,
      );
      appLogger.error(`Failed to update subscription trial days`, { error });
    },
  });

  return mutation;
};
